import React, { FC, useContext } from "react";
import MobileProgressStyled from "./MobileProgressBarStyled";
import { C2F_BREADCRUMBS, getBreadcrumbs, getCurrentMobileStepIndex, STEP_INDEX } from "~/helpers/breadcrumbs";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { ReduxState } from "~/types/redux";
import clientSide from "~/helpers/clientSide";
import useMediaQuery from "~/hooks/useMediaQuery";
import { FuelContext } from "~/providers/FuelProvider";
import { PAGE_ROUTES } from "~/constants/pages";
import { setStepChangeTo } from "~/redux/actions/breadcrumbsControl";
import { trackEvent } from "~/helpers/tracking";
import { useChatbotContext } from "~/hooks/chatbot/useChatbotContext";
import { isMobileReductionV1 } from "~/constants/mobileProgress";
import { selectToggleMobileView } from "~/redux/selectors/offerMerchData";

const MobileProgressBar: FC = React.memo(() => {
  const { router } = useContext(FuelContext);
  const dispatch = useDispatch();
  const { accordionStep, salesJourney, singleFormEnabled, c2fEnabled, pageLoading, quiz } = useSelector(
    (store: ReduxState) => ({
      accordionStep: store.breadcrumbsControl?.accordionStep,
      salesJourney: store.products?.salesJourney,
      singleFormEnabled: store.singleForm?.enabled,
      c2fEnabled: store.c2f?.enabled,
      pageLoading: store.pageLoading,
      quiz: store.quiz,
    }),
    shallowEqual
  );

  const { chatType, showChat } = useChatbotContext();
  const toggleMobileView = useSelector(selectToggleMobileView);
  const mobileReductionEnabled = isMobileReductionV1(toggleMobileView);

  const isMobile = useMediaQuery("(max-width: 767px)");

  if (!clientSide || pageLoading || !isMobile) return null;

  const isScheduleBeforeCredit = salesJourney === "scheduleBeforeCredit";

  const breadcrumbs = c2fEnabled ? C2F_BREADCRUMBS : getBreadcrumbs({ isScheduleBeforeCredit });

  const currentIndex = getCurrentMobileStepIndex(router.route, isScheduleBeforeCredit, accordionStep);

  if (currentIndex === -1 || !singleFormEnabled) return null;

  const getPlanPackagePage = () => {
    const isNextGenCart = quiz.isNextGenCart;

    if (isNextGenCart) {
      const nextGenCartPage = quiz.nextGenCartPage;
      if (nextGenCartPage === "build") return PAGE_ROUTES.BUILD;
      if (nextGenCartPage === "recommendation") return PAGE_ROUTES.RECOMMENDATION;
    }

    return PAGE_ROUTES.PLAN_PACKAGE;
  };

  const handleStepClick = async (stepName: string) => {
    // Determine if this is backward navigation
    const targetStepIndex = breadcrumbs.findIndex((step) => step.name === stepName);
    const isBackwardNavigation = targetStepIndex < currentIndex;

    trackEvent({
      action: "elementClicked",
      data: {
        elementType: "Progress Step",
        position: "progress_step",
        location: "progress_bar_mobile",
        text: stepName,
      },
    });

    const stepActions = {
      Plan: async () => {
        dispatch(setStepChangeTo(STEP_INDEX.BAU.PLAN));
        await router.push(getPlanPackagePage());
        if (isBackwardNavigation && chatType !== "DISABLED") {
          showChat("back-button");
        }
      },
      Contact: async () => {
        const stepIndex = isScheduleBeforeCredit ? STEP_INDEX.IBC.CONTACT : STEP_INDEX.BAU.CONTACT;
        dispatch(setStepChangeTo(stepIndex));
        if (accordionStep === STEP_INDEX.BAU.PAYMENT) {
          await router.replace(PAGE_ROUTES.CHECKOUT);
          if (isBackwardNavigation && chatType !== "DISABLED") {
            showChat("back-button");
          }
        }
      },
      Installation: async () => {
        const stepIndex = isScheduleBeforeCredit ? STEP_INDEX.IBC.INSTALLATION : STEP_INDEX.BAU.INSTALLATION;
        dispatch(setStepChangeTo(stepIndex));
        if (accordionStep === STEP_INDEX.BAU.PAYMENT) {
          await router.replace(PAGE_ROUTES.CHECKOUT);
          if (isBackwardNavigation && chatType !== "DISABLED") {
            showChat("back-button");
          }
        }
      },
    };

    await stepActions[stepName as keyof typeof stepActions]?.();
  };

  return (
    <MobileProgressStyled c2fEnabled={c2fEnabled} mobileReductionEnabled={mobileReductionEnabled}>
      {breadcrumbs.map((step, index) => {
        const isActive = index <= currentIndex;
        return (
          <button
            key={step.name}
            className={`mobile-progress__step ${
              isActive ? "mobile-progress__step--active" : "mobile-progress__step--inactive"
            }`}
            role={isActive ? "button" : undefined}
            tabIndex={isActive ? 0 : -1}
            onClick={isActive ? () => handleStepClick(step.name) : undefined}
          >
            <div className="mobile-progress__line" />
            <span className="mobile-progress__label">{step.name}</span>
          </button>
        );
      })}
    </MobileProgressStyled>
  );
});

MobileProgressBar.displayName = "MobileProgressBar";
export default MobileProgressBar;
